// WP-0.2 input normalizer:
//  - EX-4: amount fields accept inline arithmetic (`1200+350`).
//  - AI-6: locale parsing — "50 hazar", "5 lakh", "1.2k", "das hazar"; default currency
//    PKR unless a symbol/word indicates otherwise.
//  - §18.1: shorthand normalizes to a preview shown under the field before save.
// All arithmetic is exact BigInt rationals (FX-5) — rounding happens once, at the end,
// only for inexact division, and is reported via `rounded`.
// Relative-date parsing ("yesterday", "parson") lands with the Tier-0 grammar (WP-2.3).
import { type CurrencyCode, currencyInfo, MoneyError } from './currency';
import { type FormatOptions, formatMoney } from './format';
import { fromMinorBigInt, type Money } from './money';

export interface ParseOk {
  readonly ok: true;
  readonly money: Money;
  /** AI-6: true when a symbol/word named the currency; false when the default applied. */
  readonly explicitCurrency: boolean;
  /** True when inexact division forced rounding (half-up) at minor-unit precision. */
  readonly rounded: boolean;
  /** §18.1 normalized preview, e.g. "PKR 2,00,000". */
  readonly preview: string;
}

export interface ParseErr {
  readonly ok: false;
  readonly error: string;
}

export type ParseResult = ParseOk | ParseErr;

export interface ParseOptions extends FormatOptions {
  readonly defaultCurrency?: CurrencyCode;
}

// --- currency markers -------------------------------------------------------------

const SYMBOL_CURRENCIES: readonly (readonly [string, CurrencyCode])[] = [
  ['$', 'USD'],
  ['€', 'EUR'],
  ['£', 'GBP'],
  ['₨', 'PKR'],
];

const WORD_CURRENCIES: Readonly<Record<string, CurrencyCode>> = {
  usd: 'USD',
  dollar: 'USD',
  dollars: 'USD',
  eur: 'EUR',
  euro: 'EUR',
  euros: 'EUR',
  gbp: 'GBP',
  pound: 'GBP',
  pounds: 'GBP',
  pkr: 'PKR',
  rs: 'PKR',
  rupee: 'PKR',
  rupees: 'PKR',
  rupaye: 'PKR',
};

// --- locale vocabulary (AI-6) — grown further by the WP-2.3 utterance table ---------

const WORD_NUMBERS: Readonly<Record<string, number>> = {
  ek: 1,
  aik: 1,
  do: 2,
  teen: 3,
  char: 4,
  chaar: 4,
  panch: 5,
  paanch: 5,
  che: 6,
  chhe: 6,
  saat: 7,
  aath: 8,
  nau: 9,
  das: 10,
  dus: 10,
  bees: 20,
  tees: 30,
  chalis: 40,
  chaalis: 40,
  pachas: 50,
};

const MULTIPLIERS: Readonly<Record<string, bigint>> = {
  k: 1_000n,
  thousand: 1_000n,
  hazar: 1_000n,
  hazaar: 1_000n,
  sau: 100n,
  lakh: 100_000n,
  lakhs: 100_000n,
  lac: 100_000n,
  crore: 10_000_000n,
  crores: 10_000_000n,
  karor: 10_000_000n,
  million: 1_000_000n,
};

// --- exact rationals ---------------------------------------------------------------

interface Rational {
  readonly n: bigint; // carries the sign
  readonly d: bigint; // always > 0
}

const rat = (n: bigint, d: bigint): Rational => (d < 0n ? { n: -n, d: -d } : { n, d });
const ratAdd = (a: Rational, b: Rational): Rational => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const ratSub = (a: Rational, b: Rational): Rational => rat(a.n * b.d - b.n * a.d, a.d * b.d);
const ratMul = (a: Rational, b: Rational): Rational => rat(a.n * b.n, a.d * b.d);
const ratDiv = (a: Rational, b: Rational): Rational => {
  if (b.n === 0n) throw new MoneyError('Division by zero');
  return rat(a.n * b.d, a.d * b.n);
};

// --- tokens ------------------------------------------------------------------------

type Token =
  | { kind: 'num'; value: Rational; scale: number; folded?: boolean }
  | { kind: 'op'; op: '+' | '-' | '*' | '/' }
  | { kind: 'lparen' }
  | { kind: 'rparen' };

const OP_ALIASES: Readonly<Record<string, '+' | '-' | '*' | '/'>> = {
  '+': '+',
  '-': '-',
  '−': '-', // U+2212
  '*': '*',
  '×': '*',
  '/': '/',
  '÷': '/',
};

interface TokenizeResult {
  readonly tokens: Token[];
  readonly numberCount: number;
  readonly hadWordOrOp: boolean;
  readonly maxLiteralScale: number;
}

function tokenize(input: string): TokenizeResult {
  const tokens: Token[] = [];
  let numberCount = 0;
  let hadWordOrOp = false;
  let maxLiteralScale = 0;
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === undefined) break;
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    const op = OP_ALIASES[ch];
    if (op !== undefined) {
      tokens.push({ kind: 'op', op });
      hadWordOrOp = true;
      i += 1;
      continue;
    }
    if (ch === '(') {
      tokens.push({ kind: 'lparen' });
      hadWordOrOp = true;
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ kind: 'rparen' });
      i += 1;
      continue;
    }
    if (/\d/.test(ch)) {
      const rest = input.slice(i);
      const m = /^\d+(?:\.\d+)?/.exec(rest);
      if (!m) throw new MoneyError(`Couldn't read a number at "${rest}"`);
      const text = m[0];
      const [intPart = '0', fracPart = ''] = text.split('.');
      const scale = fracPart.length;
      tokens.push({
        kind: 'num',
        value: rat(BigInt(intPart + fracPart), 10n ** BigInt(scale)),
        scale,
      });
      numberCount += 1;
      maxLiteralScale = Math.max(maxLiteralScale, scale);
      i += text.length;
      continue;
    }
    if (/[a-z]/i.test(ch)) {
      const rest = input.slice(i);
      const m = /^[a-z]+/i.exec(rest);
      const word = (m?.[0] ?? '').toLowerCase();
      hadWordOrOp = true;
      const wordNumber = WORD_NUMBERS[word];
      const multiplier = MULTIPLIERS[word];
      if (wordNumber !== undefined) {
        tokens.push({ kind: 'num', value: rat(BigInt(wordNumber), 1n), scale: 0 });
        numberCount += 1;
      } else if (multiplier !== undefined) {
        // A multiplier must follow a number it can scale ("2 lakh", "1.5k", "das hazar").
        const prev = tokens[tokens.length - 1];
        if (prev?.kind !== 'num') {
          throw new MoneyError(`"${word}" needs a number before it (e.g. "2 ${word}")`);
        }
        if (prev.folded === true) {
          // "2 lakh lakh" — a duplicated scale word (common transcript stutter, WP-2.3)
          // would silently inflate 10^5x. Refuse rather than guess.
          throw new MoneyError(`"${word}" repeats a multiplier — write the amount once`);
        }
        tokens[tokens.length - 1] = {
          kind: 'num',
          value: ratMul(prev.value, rat(multiplier, 1n)),
          scale: prev.scale,
          folded: true,
        };
      } else {
        throw new MoneyError(`Couldn't understand "${word}"`);
      }
      i += word.length;
      continue;
    }
    throw new MoneyError(`Unexpected character "${ch}"`);
  }
  return { tokens, numberCount, hadWordOrOp, maxLiteralScale };
}

// --- recursive-descent evaluation ---------------------------------------------------

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  parse(): Rational {
    const value = this.expr();
    if (this.pos !== this.tokens.length) {
      throw new MoneyError('Unexpected trailing input');
    }
    return value;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private expr(): Rational {
    let left = this.term();
    for (;;) {
      const t = this.peek();
      if (t?.kind === 'op' && (t.op === '+' || t.op === '-')) {
        this.pos += 1;
        const right = this.term();
        left = t.op === '+' ? ratAdd(left, right) : ratSub(left, right);
      } else if (t?.kind === 'num') {
        // "1200 350" — adjacent numbers with no operator: refuse rather than guess.
        throw new MoneyError('Missing operator between numbers');
      } else {
        return left;
      }
    }
  }

  private term(): Rational {
    let left = this.factor();
    for (;;) {
      const t = this.peek();
      if (t?.kind === 'op' && (t.op === '*' || t.op === '/')) {
        this.pos += 1;
        const right = this.factor();
        left = t.op === '*' ? ratMul(left, right) : ratDiv(left, right);
      } else {
        return left;
      }
    }
  }

  private factor(): Rational {
    const t = this.peek();
    if (t === undefined) throw new MoneyError('Amount ended unexpectedly');
    if (t.kind === 'op' && (t.op === '+' || t.op === '-')) {
      this.pos += 1;
      const inner = this.factor();
      return t.op === '-' ? rat(-inner.n, inner.d) : inner;
    }
    if (t.kind === 'num') {
      this.pos += 1;
      return t.value;
    }
    if (t.kind === 'lparen') {
      this.pos += 1;
      const inner = this.expr();
      const close = this.peek();
      if (close?.kind !== 'rparen') throw new MoneyError('Missing closing parenthesis');
      this.pos += 1;
      return inner;
    }
    throw new MoneyError('Expected a number');
  }
}

// --- entry point --------------------------------------------------------------------

export function parseAmountInput(raw: string, opts?: ParseOptions): ParseResult {
  try {
    let text = raw.trim();
    if (text === '') return { ok: false, error: 'Enter an amount' };
    // Total-function contract: parseAmountInput never throws on user input. The cap
    // also bounds the recursive-descent depth (pathological '((((…' / '----…' pastes).
    if (text.length > 256) return { ok: false, error: 'Amount is too long' };

    // 1. Currency detection (AI-6): symbols first, then word markers; conflicts refuse.
    let detected: CurrencyCode | undefined;
    const claim = (c: CurrencyCode): void => {
      if (detected !== undefined && detected !== c) {
        throw new MoneyError(`Conflicting currencies: ${detected} and ${c}`);
      }
      detected = c;
    };
    for (const [symbol, code] of SYMBOL_CURRENCIES) {
      if (text.includes(symbol)) {
        claim(code);
        text = text.replaceAll(symbol, ' ');
      }
    }
    text = text.replace(/[a-z]+\.?/gi, (word) => {
      const code = WORD_CURRENCIES[word.toLowerCase().replace(/\.$/, '')];
      if (code === undefined) return word; // not a currency marker — leave for the tokenizer
      claim(code);
      return ' ';
    });

    const currency = detected ?? opts?.defaultCurrency ?? 'PKR'; // AI-6 default
    const info = currencyInfo(currency);

    // 2. Strip digit-grouping commas ("1,25,000" and "20,000" both normalize).
    text = text.replaceAll(',', '');

    // 3. Tokenize (folds shorthand multipliers and word numbers), then evaluate exactly.
    const { tokens, numberCount, hadWordOrOp, maxLiteralScale } = tokenize(text);
    if (numberCount === 0) return { ok: false, error: 'No amount found' };

    // A plain literal with more decimals than the currency supports is a typo, not
    // something to round silently ("12.345" PKR). Arithmetic results may round below.
    const isPlainLiteral = numberCount === 1 && !hadWordOrOp;
    if (isPlainLiteral && maxLiteralScale > info.decimalDigits) {
      return {
        ok: false,
        error: `${currency} amounts support ${String(info.decimalDigits)} decimal places`,
      };
    }

    const value = new Parser(tokens).parse();

    // 4. Convert to minor units; round half-up (away from zero) only if inexact.
    const scaledN = value.n * BigInt(info.minorPerMajor);
    let minor = scaledN / value.d;
    const remainder = scaledN % value.d;
    let rounded = false;
    if (remainder !== 0n) {
      rounded = true;
      const absRem = remainder < 0n ? -remainder : remainder;
      if (absRem * 2n >= value.d) {
        minor += scaledN < 0n ? -1n : 1n;
      }
    }

    const money = fromMinorBigInt(currency, minor);
    return {
      ok: true,
      money,
      explicitCurrency: detected !== undefined,
      rounded,
      preview: formatMoney(money, opts),
    };
  } catch (err) {
    if (err instanceof MoneyError) return { ok: false, error: err.message };
    throw err;
  }
}
